import { Avatar, CircularProgress, FormControlLabel, Radio, RadioGroup, SelectChangeEvent } from '@mui/material';
import IconButton from 'common/components/IconButton';
import SelectEl from 'common/components/Select';
import { TableNavButton } from 'common/styles/TableNavButton';
import React, { useEffect, useState } from 'react';
import { ReactComponent as GridIcon } from 'common/icons/four-grid.svg';
import { useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, RadialBar, RadialBarChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SmallLabelCard, ProductivityContainer, RiskContainer, DashboardSmallContainer } from '../styles/DashboardStyles';
import toFixedIfNecessary from 'common/utils/toFixedIfNecessary';
import numberWithSpaces from 'common/utils/numberWithSpaces';
import styled from '@emotion/styled';
import { ClientLineLeft, ClientLineWrapper, ClientsInfoContainer, ClientsInfoWrapper, ClientsList, ClientsListWrapper } from '../styles/DashboardInfoStyles';
import sendFetch from 'common/utils/fetch';
import UserDataTableProps from 'common/types/UserDataTableProps';
import { useAuth } from 'common/context/AuthContext';
import { stringAvatar } from 'common/utils/stringToColor';
import CustomTooltip from '../components/CustomTooltip';

const DashboardSmallLabelCard = ({ value }: { value: number }) => {

  return (
    <SmallLabelCard negative={value < 0}>
      <span className='dashboard-small-label-text'>{(value >= 0 ? '+' : '-') + toFixedIfNecessary(Math.abs(value), 2) + '%'}</span>
    </SmallLabelCard>
  );
};

const productivitySelectData = [
  {
    value: 1,
    label: 'Замовлення',
  },
  {
    value: 2,
    label: 'Проекти',
  },
  {
    value: 3,
    label: 'Користувачі',
  },
  {
    value: 4,
    label: 'Компанії',
  },
  {
    value: 5,
    label: 'Виконавці',
  },
  {
    value: 6,
    label: 'Завдання',
  },
  {
    value: 7,
    label: 'Планування',
  },
  {
    value: 8,
    label: 'Календар',
  },
  {
    value: 9,
    label: 'Фінанси',
  },
  {
    value: 10,
    label: 'Звіти',
  },
  {
    value: 11,
    label: 'Склад',
  },
  {
    value: 12,
    label: 'Маркетинг',
  },
  {
    value: 13,
    label: 'Логістика',
  },
  {
    value: 14,
    label: 'Документи',
  },
  {
    value: 15,
    label: 'Інтеграції',
  },
  {
    value: 16,
    label: 'Налаштування',
  },
  {
    value: 17,
    label: 'Допомога',
  },
  {
    value: 18,
    label: 'Контакти',
  },
];
const productivityData = [
  {
    name: 'Понеділок',
    total: 4000,
    gain: 2400,
    loss: 1600,
  },
  {
    name: 'Вівторок',
    total: 3000,
    gain: 1398,
    loss: 1602,
  },
  {
    name: 'Середа',
    total: 2000,
    gain: 9800,
    loss: 7800,
  },
  {
    name: 'Четвер',
    total: 2780,
    gain: 3908,
    loss: 1128,
  },
  {
    name: 'П\'ятниця',
    total: 1890,
    gain: 4800,
    loss: 2910,
  },
  {
    name: 'Субота',
    total: 2390,
    gain: 3800,
    loss: 1410,
  },
  {
    name: 'Неділя',
    total: 3490,
    gain: 4300,
    loss: 810,
  },
];

const riskData = [
  {
    name: null,
    uv: 100,
    fill: 'transparent',
  },
  {
    name: 'Логістичні проблеми',
    uv: 70,
    fill: 'rgba(249, 11, 182, 0.25)',
  },
  {
    name: 'Технологічні збої',
    uv: 90,
    fill: '#900BF940',
  },
  {
    name: 'Затримки у постачаннях',
    uv: 30,
    fill: '#1E0BF940',
  },
  {
    name: 'Зміна даних у замовленні',
    uv: 40,
    fill: '#0B78F940',
  },
  {
    name: 'Додавання у чернетку',
    uv: 50,
    fill: '#0BEBF940',
  },
];

const orderData = [
  {
    name: 'Page A',
    uv: 4000,
    pv: 2400,
    amt: 2400,
  },
  {
    name: 'Page B',
    uv: 3000,
    pv: 1398,
    amt: 2210,
  },
  {
    name: 'Page C',
    uv: 2000,
    pv: 9800,
    amt: 2290,
  },
  {
    name: 'Page D',
    uv: 2780,
    pv: 3908,
    amt: 2000,
  },
  {
    name: 'Page E',
    uv: 1890,
    pv: 4800,
    amt: 2181,
  },
  {
    name: 'Page F',
    uv: 2390,
    pv: 3800,
    amt: 2500,
  },
  {
    name: 'Page G',
    uv: 3490,
    pv: 4300,
    amt: 2100,
  },
  {
    name: 'Page H',
    uv: 2490,
    pv: 6300,
    amt: 1100,
  },
];

const clientCountData = [
  {
    name: 'Page A',
    uv: 4000,
    pv: 2400,
    amt: 2400,
  },
  {
    name: 'Page B',
    uv: 3000,
    pv: 1398,
    amt: 2210,
  },
  {
    name: 'Page C',
    uv: 2000,
    pv: 9800,
    amt: 2290,
  },
  {
    name: 'Page D',
    uv: 2780,
    pv: 3908,
    amt: 2000,
  },
  {
    name: 'Page E',
    uv: 1890,
    pv: 4800,
    amt: 2181,
  },
  {
    name: 'Page F',
    uv: 2390,
    pv: 3800,
    amt: 2500,
  },
  {
    name: 'Page G',
    uv: 3490,
    pv: 4300,
    amt: 2100,
  },
  {
    name: 'Page H',
    uv: 2490,
    pv: 6300,
    amt: 1100,
  },
];

const ClientsTopWrapper = styled.div`
    padding: 11px 16px 16px 11px;
    display:flex;
    flex-direction: column;
    flex-grow:1;
    flex-basis: 55%;
    border-radius: 16px;
    border: 1px solid ${(props) => props.theme.colors.gray};
    position: relative;
`;
const ClientsTopContainer = styled.div`
    position: absolute;
    display:flex;
    top: 11px;
    left: 11px;
    right: 16px;
    bottom: 16px;
`;
const ClientsBottomWrapper = styled.div`
    padding: 16px;
    display:flex;
    flex-direction: column;
    border-radius: 16px;
    flex: 1;
    border: 1px solid ${(props) => props.theme.colors.gray};
    position: relative;
`;
const ClientsBottomContainer = styled.div`
    position: absolute;
    display:flex;
    top: 16px;
    left: 16px;
    right: 16px;
    bottom: 16px;
    flex-direction: column;
    gap: 24px;
`;

const ClientLine = ({ name, avatar, selected, onClick }: { name: string, avatar: string | React.ReactNode, selected: boolean, onClick: () => void }) => {
  return (
        <ClientLineWrapper onClick={onClick} className={selected ? 'active' : ''}>
            <ClientLineLeft>
                {avatar}
                <p className='new-p'>{name}</p>
            </ClientLineLeft>
            <div className='dashboard-rating-wrapper'>
            </div>
        </ClientLineWrapper>
  );
};

export default function DashboardManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [productivityType, setProductivityType] = useState(1);
  const [productivityRadio, setProductivityRadio] = useState('total');

  const handleProductivityRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProductivityRadio((event.target as HTMLInputElement).value);
  };
  const type = searchParams.get('type') || 'overview';
  const productivityDataTotal = productivityData.reduce((acc, item) => acc + item.total, 0);
  const { companyId } = useAuth();
  const [clientList, setClientList] = useState<UserDataTableProps[]>([]);
  const [selectedClient, setSelectedClient] = useState<number | string>(null);
  const [selectedProject, setSelectedProject] = React.useState<number | null>(0);
  const onProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };
  const [clientListLoading, setClientListLoading] = useState(false);
  const handleClientClick = (id: number | string) => {
    setSelectedClient(id);
  };
  useEffect(() => {
    const fetchTableData = async () => {
      setClientListLoading(true);
      const formData = new FormData();
      formData.append('companyId', companyId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: [number, any] = await sendFetch('https://api-workspace.astin.net/api/user/get-client-list', {
        method: 'POST',
        data: formData,
      });
      console.log(response);
      if (response[0] === 200) {
        setClientListLoading(false);
        const newList = response[1].map((item: UserDataTableProps) => {
          const randomRating = Math.random() * (5 - 1) + 1;
          return {
            ...item,
            rating: toFixedIfNecessary(randomRating, 1),
          };
        });
        setClientList(newList);
      } else if (response[1] = 'Empty list') {
        setClientListLoading(false);
      }
    };
    if (companyId) {
      fetchTableData();
    }
  }, [type, companyId]);
  return (
    <>
      <div className='dashboard-nav-wrapper'>
        <TableNavButton className={type === 'overview' ? 'active' : ''} onClick={() => setSearchParams({ type: 'overview' })}>Загальне</TableNavButton>
        <TableNavButton className={type === 'projects' ? 'active' : ''} onClick={() => setSearchParams({ type: 'projects' })}>Проекти</TableNavButton>
      </div>
      {type !== 'projects' ? <div className='dashboard-content-wrapper'>
        <div className='dashboard-overview-top-wrapper'>
          <ProductivityContainer>
            <div className='dashboard-productivity-top-line'>
              <span className='big-text'>Продуктивність</span>
              <div className='dashboard-productivity-button-wrapper'>
                <span className='dashboard-big-text'>Розділи:</span>
                <SelectEl height={'40px'} value={productivityType} data={productivitySelectData} onChange={(event: SelectChangeEvent) => { setProductivityType(parseInt(event.target.value) as number); }}></SelectEl>
                <IconButton size={36}>
                  <GridIcon className='svg-black' />
                </IconButton>
              </div>
            </div>
            <div className='dashboard-productivity-revenue-wrapper'>
              <h2 className='new-h2'>{numberWithSpaces(productivityDataTotal)} ₴</h2>
              <span className='dashboard-big-text'>Загальна виручка</span>
            </div>
            <div className='dashboard-graph-wrapper'>
              <div className='dashboard-graph-absolute-wrapper'>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={productivityData}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 0,
                    }}
                    barSize={56}

                  >
                    <XAxis strokeDasharray="3 3" dataKey="name" scale="point" padding={{ left: 50, right: 50 }} />
                    <YAxis tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <Bar activeBar={<Rectangle fill="rgba(30, 11, 249, 1)" />} radius={[8, 8, 0, 0]} dataKey={productivityRadio} fill="rgba(30, 11, 249, 0.50)" background={{ fill: 'rgba(155, 158, 171, 0.15)', radius: '8px 8px 0 0' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className='dashboard-productivity-radio-line'>
              <p className='new-p'>Параметри:</p>
              <div className='dashboard-productivity-radio-wrapper'>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={productivityRadio}
                  onChange={handleProductivityRadioChange}
                  sx={{ display: 'flex', flexDirection: 'row' }}
                >
                  <FormControlLabel value="total" control={<Radio />} label="Загальна виручка" />
                  <FormControlLabel value="gain" control={<Radio />} label="Прибуток" />
                  <FormControlLabel value="loss" control={<Radio />} label="Витрати" />
                </RadioGroup>
              </div>
            </div>
          </ProductivityContainer>
          <RiskContainer>
            <span className='big-text'>Показник ризиків</span>
            <span style={{ marginTop: '4px' }} className='dashboard-big-text'>Замовлення</span>
            <div style={{ maxHeight: '500px' }} className='dashboard-graph-wrapper'>
              <div className='dashboard-graph-absolute-wrapper'>
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart startAngle={360 + 90} endAngle={90} innerRadius="15%" outerRadius="100%" barSize={15} data={riskData}>
                    <RadialBar
                      label={false}
                      background={false}
                      dataKey="uv"
                      maxBarSize={100}
                      cornerRadius={15}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className='dashboard-legend-wrapper'>
              {riskData.map((item, index) => {
                if (item.name === null) return null;
                return (
                  <div key={index} className='dashboard-legend-line'>
                    <div className='dashboard-legend-line-left'>
                      <div style={{ backgroundColor: item.fill }} className='dashboard-legend-color'></div>
                      <span className='dashboard-legend-text'>{item.name}</span>
                    </div>
                    <span className='dashboard-legend-text'>{item.uv}%</span>
                  </div>
                );
              })}
            </div>
          </RiskContainer>
        </div>
        <div className='dashboard-overview-bottom-wrapper'>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>{numberWithSpaces(productivityDataTotal / 2)} ₴</h2>
                  <DashboardSmallLabelCard value={2.65} />
                </div>
                <span className='dashboard-big-text'>Загальний дохід</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={300}
                      height={300}
                      data={productivityData}
                      margin={{
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                      }}
                      barSize={22}

                    >
                      <Bar radius={[8, 8, 0, 0]} dataKey="loss" fill="#0BEBF940" background={{ fill: 'transparent' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>{numberWithSpaces(5643)}</h2>
                  <DashboardSmallLabelCard value={-0.82} />
                </div>
                <span className='dashboard-big-text'>Замовлення продуктів</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart width={300} height={100} data={orderData}>
                      <Line type="monotone" dot={{ fill: '#F90BB666', height: 4, r: 2, width: 4 }} dataKey="pv" stroke="#F90BB666" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>{numberWithSpaces(45254)}</h2>
                  <DashboardSmallLabelCard value={-6.24} />
                </div>
                <span className='dashboard-big-text'>Кількість клієнтів</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart width={300} height={100} data={clientCountData}>
                      <Line type="monotone" dot={{ fill: '#900BF966', height: 4, r: 2, width: 4 }} dataKey="uv" stroke="#900BF966" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>+12,58%</h2>
                  <DashboardSmallLabelCard value={10.51} />
                </div>
                <span className='dashboard-big-text'>Загальний дохід</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={300}
                      height={300}
                      data={orderData}
                      margin={{
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                      }}
                      barSize={22}

                    >
                      <Bar radius={[8, 8, 0, 0]} dataKey="pv" fill="#0B78F966" background={{ fill: 'transparent' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
        </div>
      </div> : <div className='dashboard-content-wrapper'>
        <ClientsBottomWrapper>
          <ClientsBottomContainer>
            <div className='dashboard-meetings-line'>
              <span className='big-text'>Термін реалізації</span>
              <div style={{ width: 'unset' }} className='dashboard-message-top-line'>
                  <span className='dashboard-message-top-line-text'>
                    Проект:
                  </span>
                  <SelectEl
                    placeholder='Оберіть проект'
                    data={[
                      {
                        label: 'Проект 1',
                        value: 1,
                      },
                      {
                        label: 'Проект 2',
                        value: 2,
                      },
                      {
                        label: 'Проект 3',
                        value: 3,
                      },
                    ]}
                    value={selectedProject}
                    onChange={onProjectChange}
                  />
                </div>
            </div>
          </ClientsBottomContainer>
        </ClientsBottomWrapper>
        <ClientsTopWrapper style={{ maxHeight: '283px' }}>
          <ClientsTopContainer>
            <ClientsListWrapper>
              <ClientsList className='u-fancy-scrollbar'>
                {!clientListLoading ? clientList.map((item, index) => {
                  return (
                    <ClientLine
                      key={index}
                      name={item.companyName ? item.companyName : `${item.lastName} ${item.firstName} ${item.patronymic}`}
                      avatar={<Avatar {...stringAvatar(item.companyName ? item.companyName : `${item.lastName} ${item.firstName}`)} />}
                      selected={selectedClient === item.id}
                      onClick={() => handleClientClick(item.id)}
                    />
                  );
                }) :
                  <div className='dashboard-loading-wrapper'>
                    <CircularProgress sx={{
                      color: theme => theme.colors.primaryColor,
                    }} />
                  </div>
                }
              </ClientsList>
            </ClientsListWrapper>
            <ClientsInfoWrapper>
              <ClientsInfoContainer>
                <div className='dashboard-client-info-top-line'>
                  <span className='big-text'>Індекс ефективності</span>
                  <IconButton>
                    <GridIcon className='svg-black' />
                  </IconButton>
                </div>
                <div className='dashboard-client-info-graph-wrapper'>
                  <div className='dashboard-client-info-graph-container'>
                    <div className='dashboard-client-info-graph'>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          width={500}
                          height={300}
                          data={productivityData}
                          margin={{
                            top: 0,
                            right: 0,
                            left: 0,
                            bottom: 0,
                          }}
                          barSize={56}

                        >
                          <XAxis strokeDasharray="3 3" dataKey="name" scale="point" padding={{ left: 50, right: 50 }} />
                          <YAxis tickLine={false} axisLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <CartesianGrid vertical={false} strokeDasharray="3 3" />
                          <Bar activeBar={<Rectangle fill="rgba(249, 11, 182, 0.40)" />} radius={[8, 8, 0, 0]} dataKey={'total'} fill="rgba(249, 11, 182, 0.40)" background={{ fill: 'rgba(155, 158, 171, 0.15)', radius: '8px 8px 0 0' }} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className='dashboard-client-info-graph-info'>
                    <div className='dashboard-client-info-graph-line'>
                      <span className='dashboard-client-info-graph-big-title'>{numberWithSpaces(46)}%</span>
                      <span className='dashboard-rating-text'>Середній показник єфективності</span>
                    </div>
                    <div className='dashboard-client-info-graph-line'>
                      <span className='dashboard-client-info-graph-big-title'>{numberWithSpaces(80)}%</span>
                      <span className='dashboard-rating-text'>Найвищий показник за тиждень</span>
                    </div>
                  </div>
                </div>
              </ClientsInfoContainer>
            </ClientsInfoWrapper>

          </ClientsTopContainer>
        </ClientsTopWrapper>
      </div>}
    </>
  );
}